import React, { useState, useMemo } from 'react';
import { Button } from '@components/common/button';
import { Card } from '@components/common/card';
import { Table, TableColumn } from '@components/common/table';
import { Input } from '@components/common/input';
import { LoadingSpinner } from '@components/common/loading_spinner';
import {
  Download,
  Printer,
  Mail,
  Calendar,
  Filter,
  FileText,
  DollarSign,
  TrendingUp,
  TrendingDown,
  AlertCircle,
  CheckCircle,
  Clock,
  ArrowLeft,
  Search
} from 'lucide-react';

// Types
interface StatementTransaction {
  id: string | number;
  date: string;
  type: 'invoice' | 'payment' | 'credit_note' | 'debit_note';
  reference: string;
  description: string;
  debit: number;
  credit: number;
  balance: number;
  status?: 'paid' | 'pending' | 'overdue' | 'cancelled';
}

interface ClientStatementProps {
  client: {
    id: string | number;
    name: string;
    email?: string;
    phone_number?: string;
    address?: string;
  };
  transactions: StatementTransaction[];
  opening_balance?: number;
  closing_balance?: number;
  period_start?: string;
  period_end?: string;
  is_loading?: boolean;
  on_back?: () => void;
  on_download?: () => void;
  on_print?: () => void;
  on_email?: () => void;
}

export const ClientStatement: React.FC<ClientStatementProps> = ({
  client,
  transactions = [],
  opening_balance = 0,
  closing_balance = 0,
  period_start,
  period_end,
  is_loading = false,
  on_back,
  on_download,
  on_print,
  on_email
}) => {
  const [search_term, set_search_term] = useState('');
  const [filter_type, set_filter_type] = useState<'all' | 'invoice' | 'payment'>('all');
  const [date_from, set_date_from] = useState(period_start || '');
  const [date_to, set_date_to] = useState(period_end || '');

  // Calculate summary
  const summary = useMemo(() => {
    const filtered = transactions.filter(t => {
      if (filter_type !== 'all' && t.type !== filter_type) return false;
      if (date_from && new Date(t.date) < new Date(date_from)) return false;
      if (date_to && new Date(t.date) > new Date(date_to)) return false;
      if (search_term) {
        const term = search_term.toLowerCase();
        return (
          t.reference.toLowerCase().includes(term) ||
          t.description.toLowerCase().includes(term)
        );
      }
      return true;
    });

    const total_debits = filtered.reduce((sum, t) => sum + t.debit, 0);
    const total_credits = filtered.reduce((sum, t) => sum + t.credit, 0);
    
    return {
      total_debits,
      total_credits,
      net_balance: total_debits - total_credits,
      transaction_count: filtered.length
    };
  }, [transactions, filter_type, date_from, date_to, search_term]);

  // Filter transactions
  const filtered_transactions = useMemo(() => {
    return transactions.filter(t => {
      if (filter_type !== 'all' && t.type !== filter_type) return false;
      if (date_from && new Date(t.date) < new Date(date_from)) return false;
      if (date_to && new Date(t.date) > new Date(date_to)) return false;
      if (search_term) {
        const term = search_term.toLowerCase();
        return (
          t.reference.toLowerCase().includes(term) ||
          t.description.toLowerCase().includes(term)
        );
      }
      return true;
    }).sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());
  }, [transactions, filter_type, date_from, date_to, search_term]);

  // Get transaction type badge
  const get_type_badge = (type: StatementTransaction['type']) => {
    const config = {
      invoice: { label: 'Invoice', bg: 'bg-blue-100', text: 'text-blue-800', icon: FileText },
      payment: { label: 'Payment', bg: 'bg-green-100', text: 'text-green-800', icon: DollarSign },
      credit_note: { label: 'Credit Note', bg: 'bg-purple-100', text: 'text-purple-800', icon: TrendingDown },
      debit_note: { label: 'Debit Note', bg: 'bg-orange-100', text: 'text-orange-800', icon: TrendingUp }
    };

    const { label, bg, text, icon: Icon } = config[type];

    return (
      <span className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-semibold ${bg} ${text}`}>
        <Icon size={12} />
        {label}
      </span>
    );
  };

  // Get status badge
  const get_status_badge = (status?: StatementTransaction['status']) => {
    if (!status) return null;

    const config = {
      paid: { label: 'Paid', bg: 'bg-success', icon: CheckCircle },
      pending: { label: 'Pending', bg: 'bg-warning', icon: Clock },
      overdue: { label: 'Overdue', bg: 'bg-error', icon: AlertCircle },
      cancelled: { label: 'Cancelled', bg: 'bg-gray-500', icon: AlertCircle }
    };

    const { label, bg, icon: Icon } = config[status];

    return (
      <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold ${bg} bg-opacity-10 text-${bg.replace('bg-', '')}`}>
        <Icon size={12} />
        {label}
      </span>
    );
  };

  // Table columns
  const columns: TableColumn<StatementTransaction>[] = [
    {
      key: 'date',
      header: 'Date',
      sortable: true,
      render: (t) => (
        <div className="text-sm">
          <p className="font-medium text-charcoal">
            {new Date(t.date).toLocaleDateString('en-US', { 
              month: 'short', 
              day: 'numeric', 
              year: 'numeric' 
            })}
          </p>
        </div>
      )
    },
    {
      key: 'reference',
      header: 'Reference',
      render: (t) => (
        <div>
          <p className="font-medium text-charcoal text-sm">{t.reference}</p>
          <p className="text-xs text-gray-500">{get_type_badge(t.type)}</p>
        </div>
      )
    },
    {
      key: 'description',
      header: 'Description',
      render: (t) => (
        <div className="max-w-xs">
          <p className="text-sm text-charcoal truncate">{t.description}</p>
          {t.status && <div className="mt-1">{get_status_badge(t.status)}</div>}
        </div>
      )
    },
    {
      key: 'debit',
      header: 'Charges',
      align: 'right',
      render: (t) => (
        t.debit > 0 ? (
          <p className="font-semibold text-error">
            KES {t.debit.toLocaleString()}
          </p>
        ) : (
          <p className="text-gray-400">-</p>
        )
      )
    },
    {
      key: 'credit',
      header: 'Payments',
      align: 'right',
      render: (t) => (
        t.credit > 0 ? (
          <p className="font-semibold text-success">
            KES {t.credit.toLocaleString()}
          </p>
        ) : (
          <p className="text-gray-400">-</p>
        )
      )
    },
    {
      key: 'balance',
      header: 'Balance',
      align: 'right',
      render: (t) => (
        <p className={`font-bold ${t.balance > 0 ? 'text-error' : t.balance < 0 ? 'text-success' : 'text-charcoal'}`}>
          KES {Math.abs(t.balance).toLocaleString()}
          {t.balance < 0 && ' CR'}
        </p>
      )
    }
  ];

  if (is_loading) {
    return (
      <div className="min-h-screen  flex items-center justify-center">
        <LoadingSpinner size="lg" text="Loading statement..." />
      </div>
    );
  }

  return (
    <div className="min-h-screen ">
      {/* Header */}
      <div className="bg-white border-b border-gray-200 sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              {on_back && (
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={on_back}
                  icon_left={<ArrowLeft size={18} />}
                >
                  Back
                </Button>
              )}
              <div>
                <h1 className="text-2xl font-bold text-charcoal">Account Statement</h1>
                <p className="text-sm text-gray-500 mt-0.5">{client.name}</p>
              </div>
            </div>
            
            <div className="flex items-center gap-2">
              {on_email && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={on_email}
                  icon_left={<Mail size={16} />}
                >
                  Email
                </Button>
              )}
              {on_print && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={on_print}
                  icon_left={<Printer size={16} />}
                >
                  Print
                </Button>
              )}
              {on_download && (
                <Button
                  variant="primary"
                  size="sm"
                  onClick={on_download}
                  icon_left={<Download size={16} />}
                >
                  Download PDF
                </Button>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-6 py-8 space-y-6">
        {/* Client Info Card */}
        <Card padding="md">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <h3 className="text-sm font-semibold text-gray-500 mb-3">Client Information</h3>
              <div className="space-y-2">
                <p className="text-base font-bold text-charcoal">{client.name}</p>
                {client.email && <p className="text-sm text-gray-600">{client.email}</p>}
                {client.phone_number && <p className="text-sm text-gray-600">{client.phone_number}</p>}
                {client.address && <p className="text-sm text-gray-600">{client.address}</p>}
              </div>
            </div>
            <div>
              <h3 className="text-sm font-semibold text-gray-500 mb-3">Statement Period</h3>
              <div className="space-y-2">
                {period_start && period_end && (
                  <p className="text-base font-medium text-charcoal">
                    {new Date(period_start).toLocaleDateString()} - {new Date(period_end).toLocaleDateString()}
                  </p>
                )}
                <p className="text-sm text-gray-600">
                  Generated on {new Date().toLocaleDateString()}
                </p>
              </div>
            </div>
          </div>
        </Card>

        {/* Summary Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <Card padding="md">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-lg bg-blue-100 text-blue-600 flex items-center justify-center">
                <FileText size={24} />
              </div>
              <div>
                <p className="text-sm text-gray-500">Opening Balance</p>
                <p className="text-xl font-bold text-charcoal">
                  KES {opening_balance.toLocaleString()}
                </p>
              </div>
            </div>
          </Card>

          <Card padding="md">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-lg bg-error bg-opacity-10 text-error flex items-center justify-center">
                <TrendingUp size={24} />
              </div>
              <div>
                <p className="text-sm text-gray-500">Total Charges</p>
                <p className="text-xl font-bold text-error">
                  KES {summary.total_debits.toLocaleString()}
                </p>
              </div>
            </div>
          </Card>

          <Card padding="md">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-lg bg-success bg-opacity-10 text-success flex items-center justify-center">
                <TrendingDown size={24} />
              </div>
              <div>
                <p className="text-sm text-gray-500">Total Payments</p>
                <p className="text-xl font-bold text-success">
                  KES {summary.total_credits.toLocaleString()}
                </p>
              </div>
            </div>
          </Card>

          <Card padding="md">
            <div className="flex items-center gap-3">
              <div className={`w-12 h-12 rounded-lg flex items-center justify-center ${
                closing_balance > 0 ? 'bg-warning bg-opacity-10 text-warning' : 'bg-success bg-opacity-10 text-success'
              }`}>
                <DollarSign size={24} />
              </div>
              <div>
                <p className="text-sm text-gray-500">Closing Balance</p>
                <p className={`text-xl font-bold ${closing_balance > 0 ? 'text-warning' : 'text-success'}`}>
                  KES {Math.abs(closing_balance).toLocaleString()}
                  {closing_balance < 0 && ' CR'}
                </p>
              </div>
            </div>
          </Card>
        </div>

        {/* Filters */}
        <Card padding="md">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <Input
              type="text"
              placeholder="Search by reference or description..."
              value={search_term}
              onChange={(e) => set_search_term(e.target.value)}
              icon_left={<Search size={18} />}
            />

            <div className="flex items-center gap-2 bg-white px-3 py-2 rounded-lg border border-gray-300">
              <Filter size={16} className="text-gray-500" />
              <select
                value={filter_type}
                onChange={(e) => set_filter_type(e.target.value as any)}
                className="text-sm border-none focus:outline-none cursor-pointer flex-1"
              >
                <option value="all">All Types</option>
                <option value="invoice">Invoices Only</option>
                <option value="payment">Payments Only</option>
              </select>
            </div>

            <Input
              type="date"
              placeholder="From Date"
              value={date_from}
              onChange={(e) => set_date_from(e.target.value)}
              icon_left={<Calendar size={18} />}
            />

            <Input
              type="date"
              placeholder="To Date"
              value={date_to}
              onChange={(e) => set_date_to(e.target.value)}
              icon_left={<Calendar size={18} />}
            />
          </div>
        </Card>

        {/* Transactions Table */}
        <Card padding="none">
          <div className="px-6 py-4 border-b border-gray-200">
            <h2 className="text-lg font-bold text-charcoal">Transaction History</h2>
            <p className="text-sm text-gray-500 mt-1">
              {summary.transaction_count} transaction{summary.transaction_count !== 1 ? 's' : ''}
            </p>
          </div>
          
          <Table
            data={filtered_transactions}
            columns={columns}
            empty_message="No transactions found for the selected period"
            striped
            hoverable
          />
        </Card>

        {/* Footer Summary */}
        <Card padding="md">
          <div className="flex justify-between items-center">
            <div>
              <p className="text-sm text-gray-500">Statement Summary</p>
              <p className="text-xs text-gray-400 mt-1">
                {summary.transaction_count} transactions processed
              </p>
            </div>
            <div className="text-right">
              <p className="text-sm text-gray-500 mb-1">Current Balance Due</p>
              <p className={`text-2xl font-bold ${closing_balance > 0 ? 'text-error' : closing_balance < 0 ? 'text-success' : 'text-charcoal'}`}>
                KES {Math.abs(closing_balance).toLocaleString()}
                {closing_balance < 0 && ' CR'}
              </p>
            </div>
          </div>
        </Card>
      </div>
    </div>
  );
};